import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import MatrixBackground from "../components/MatrixBackground";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, Store, Bike } from "lucide-react";

const ROLES = [
  { id: "customer", label: "Customer", desc: "Shop in 10 mins", icon: ShoppingBag },
  { id: "merchant", label: "Merchant", desc: "Sell from your shop", icon: Store },
  { id: "rider", label: "Rider", desc: "Deliver and earn", icon: Bike },
];

function roleToPath(role) {
  if (role === "merchant") return "/merchant";
  if (role === "rider") return "/rider";
  return "/app";
}

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!/^[6-9]\d{9}$/.test(form.phone)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success("Welcome to STACK ⚡");
    navigate(roleToPath(res.user.role), { replace: true });
  };

  return (
    <div className="min-h-screen grid-bg noise isolate flex items-center justify-center p-4" data-testid="register-container">
      <MatrixBackground opacity={0.25} />
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="inline-flex items-center gap-1 text-slate-400 hover:text-cyan-300 text-sm mb-6" data-testid="back-home">
          <ArrowLeft size={14} /> Back to home
        </Link>
        <div className="glass rounded-3xl p-7 ring-cyan">
          <div className="flex items-center justify-between">
            <Logo size={34} />
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Create account</div>
          </div>
          <h1 className="heading-font text-3xl font-extrabold mt-6">Join the stack.</h1>
          <p className="text-slate-400 text-sm mt-1">Pick your role and get going.</p>

          <div className="grid grid-cols-3 gap-2 mt-5" data-testid="role-selector">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = form.role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => update("role", r.id)}
                  data-testid={`role-${r.id}`}
                  className={`rounded-2xl p-3 text-left border transition ${
                    active
                      ? "border-cyan-400/60 bg-cyan-400/10 ring-cyan"
                      : "border-cyan-500/10 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  <Icon size={18} className={active ? "text-cyan-300" : "text-slate-300"} />
                  <div className={`text-sm font-bold mt-2 ${active ? "text-white" : "text-slate-200"}`}>{r.label}</div>
                  <div className="text-[11px] text-slate-400">{r.desc}</div>
                </button>
              );
            })}
          </div>

          <form onSubmit={onSubmit} className="space-y-4 mt-5" data-testid="register-form">
            <div>
              <Label htmlFor="name" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Full name</Label>
              <Input id="name" required value={form.name} onChange={(e) => update("name", e.target.value)}
                     className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                     placeholder="Jane Doe" data-testid="register-name" />
            </div>
            <div>
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)}
                     className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                     placeholder="you@email.com" data-testid="register-email" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Mobile number</Label>
              <div className="mt-2 flex gap-2">
                <div className="h-11 px-3 inline-flex items-center rounded-md border border-cyan-500/15 bg-white/5 text-slate-400 text-sm">+91</div>
                <Input id="phone" inputMode="numeric" maxLength={10} required value={form.phone}
                       onChange={(e) => update("phone", e.target.value.replace(/\D/g, ""))}
                       className="h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                       placeholder="98765 43210" data-testid="register-phone" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Password</Label>
              <Input id="password" type="password" required value={form.password} onChange={(e) => update("password", e.target.value)}
                     className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                     placeholder="At least 6 chars" data-testid="register-password" />
            </div>
            <Button type="submit" className="btn-neon w-full h-11 rounded-full" disabled={loading} data-testid="register-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-5 text-sm text-slate-400 text-center">
            Already on STACK?{" "}
            <Link to="/login" className="text-cyan-300 font-semibold" data-testid="goto-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
